import { useState, useEffect, useRef, useMemo } from "react";

import { format } from "date-fns";
import { de } from "date-fns/locale";

import { useBooking } from "../../store/bookingStore";
import { parseLocalDateString } from "../../util/parseLocalDate";
import {
  formatLocalDate,
  getAvailabilityRangesForDate,
  getBookingDurationHours,
  getBusyIntervalItems,
  hasBookableTimeSlot,
  isBookingRangeAvailable,
} from "../../util/studioAvailability";
import classes from "./BookingFirst.module.css"
import TotalBox from "./TotalBox";
import DateChooser from "./BookingPhone/DateChooser";

export const BookingFirst = ({ 
    setDate, startTime, setStartTime, endTime, 
    setEndTime, setNumberOfPeople, 
    studioPrice, 
    minHours,
    studio,
    bookingStudioData,
    setShowPackageModal,
    setPackageModalFromOverlay,
    studioCreativesData,
}) => {

  const [times, setTimes] = useState([]);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [showPeopleDropdown, setShowPeopleDropdown] = useState(false);
  const [showPackageDropdown, setShowPackageDropdown] = useState(false);
  const datePickerRef = useRef(null);
  const peopleDropdownRef = useRef(null);

  const { bookingData, updateBookingData } = useBooking();

  const [totalPriceState, setTotalPriceState] = useState(0);

  // Detect if this studio uses package-based pricing
  // IMPORTANT:
  // - Only treat the studio as "package pricing" when pricingMode is explicitly "PACKAGES"
  // - Studios with pricingMode === "HOURLY" must stay hourly even if packages exist
  const isPackagePricing = useMemo(
    () => studio?.pricingMode === 'PACKAGES',
    [studio?.pricingMode]
  );

  // Normalize studio packages and derive people groups (same logic as in StudioProfile/Booking.jsx)
  const normalizedPackages = useMemo(() => {
    if (!isPackagePricing) return [];
    const pkgs = Array.isArray(studio?.packages) ? studio.packages : [];
    return [...pkgs].sort((a, b) => {
      const aHours = a?.durationHours ?? 0;
      const bHours = b?.durationHours ?? 0;
      return aHours - bHours;
    });
  }, [isPackagePricing, studio?.packages]);

  const personGroups = useMemo(() => {
    if (!isPackagePricing) return [];
    const map = new Map();
    normalizedPackages.forEach((pkg) => {
      if (!pkg?.prices || !Array.isArray(pkg.prices)) return;
      pkg.prices.forEach((p) => {
        if (!p) return;
        const min = typeof p.minPersons === 'number' ? p.minPersons : parseInt(p.minPersons || '0', 10);
        const max = typeof p.maxPersons === 'number' ? p.maxPersons : parseInt(p.maxPersons || '0', 10);
        if (!min || !max) return;
        const key = `${min}-${max}`;
        if (!map.has(key)) {
          map.set(key, { min, max, key });
        }
      });
    });
    const groups = Array.from(map.values());
    groups.sort((a, b) => a.min - b.min);
    return groups;
  }, [isPackagePricing, normalizedPackages]);

  const getSelectedPackage = () => {
    if (!isPackagePricing || normalizedPackages.length === 0) return null;
    const currentId = bookingData?.packageId || normalizedPackages[0]?.id;
    const found = normalizedPackages.find((pkg) => pkg.id === currentId);
    return found || normalizedPackages[0];
  };

  const getPackageBasePriceForGroup = (pkg, groupKey) => {
    if (!pkg || !Array.isArray(pkg.prices) || pkg.prices.length === 0) return 0;

    let priceEntry = null;

    if (groupKey) {
      const [minStr, maxStr] = groupKey.split('-');
      const min = parseInt(minStr || '0', 10);
      const max = parseInt(maxStr || '0', 10);

      priceEntry = pkg.prices.find((p) => {
        const pMin = typeof p.minPersons === 'number' ? p.minPersons : parseInt(p.minPersons || '0', 10);
        const pMax = typeof p.maxPersons === 'number' ? p.maxPersons : parseInt(p.maxPersons || '0', 10);
        return pMin === min && pMax === max;
      });
    }

    if (!priceEntry) {
      priceEntry = pkg.prices[0];
    }

    const rawPrice = priceEntry?.price;
    const num = typeof rawPrice === 'number' ? rawPrice : parseFloat(rawPrice || '0');
    return Number.isFinite(num) ? num : 0;
  };

  const getAdditionalCostLabelForGroup = (pkg, groupKey) => {
    if (!pkg || !Array.isArray(pkg.prices) || pkg.prices.length === 0) return '';

    const lowestPriceEntry = pkg.prices[0];
    const rawLowest = lowestPriceEntry?.price;
    const lowest = typeof rawLowest === 'number' ? rawLowest : parseFloat(rawLowest || '0');

    const groupBase = getPackageBasePriceForGroup(pkg, groupKey);
    const extra = Math.max(0, groupBase - lowest);

    if (!extra) return '';
    return ` (+${extra} €)`;
  };

  // Derive simple labels for package and people for display in the desktop summary
  const packageLabel = useMemo(() => {
    if (bookingData?.packageDurationHours) {
      const h = bookingData.packageDurationHours;
      return `${h} ${h === 1 ? 'Stunde' : 'Stunden'}`;
    }
    if (bookingData?.packageName) return bookingData.packageName;
    return 'Paket auswählen';
  }, [bookingData?.packageDurationHours, bookingData?.packageName]);

  const packagePriceLabel = useMemo(() => {
    // Show the cheapest studio package price (smallest people group) as the base price
    if (!isPackagePricing) return '';
    const pkg = getSelectedPackage();
    if (!pkg || !Array.isArray(pkg.prices) || pkg.prices.length === 0) return '';
    const lowestPriceEntry = pkg.prices[0];
    const rawPrice = lowestPriceEntry?.price;
    const num = typeof rawPrice === 'number' ? rawPrice : parseFloat(rawPrice || '0');
    return Number.isFinite(num) && num > 0 ? `${num} €` : '';
  }, [isPackagePricing, bookingData?.packageId, personGroups]);

  const getPackageBasePrice = (pkg) => {
    if (!pkg || !Array.isArray(pkg.prices) || pkg.prices.length === 0) return 0;
    // Keep package list prices independent from selected people group.
    const lowestPriceEntry = pkg.prices[0];
    const rawPrice = lowestPriceEntry?.price;
    const num = typeof rawPrice === 'number' ? rawPrice : parseFloat(rawPrice || '0');
    return Number.isFinite(num) ? num : 0;
  };

  const peopleLabel = useMemo(() => {
    if (bookingData?.selectedPeopleGroupKey) {
      const [min, max] = bookingData.selectedPeopleGroupKey.split('-');
      if (min && max) {
        const pkg = getSelectedPackage();
        const extraLabel = getAdditionalCostLabelForGroup(pkg, bookingData.selectedPeopleGroupKey);
        return `${min} - ${max} Personen${extraLabel}`;
      }
    }
    if (bookingData?.numberOfPeople) {
      // numberOfPeople might already be a range like "1-5"
      const value = String(bookingData.numberOfPeople);
      return value.includes('Person') ? value : `${value} Personen`;
    }
    return '1-5 Personen';
  }, [bookingData?.selectedPeopleGroupKey, bookingData?.numberOfPeople, bookingData?.packageId, personGroups]);
  
  // Ensure a default studio package is selected in package-pricing mode
  // so that bookingData.packageId is never empty when user proceeds to checkout.
  useEffect(() => {
    if (!isPackagePricing) return;
    if (!normalizedPackages.length) return;

    // If no packageId stored yet, default to the first (cheapest duration) package
    if (!bookingData?.packageId) {
      const defaultPkg = normalizedPackages[0];
      const duration = defaultPkg?.durationHours;
      const name =
        defaultPkg?.name ||
        (typeof duration === 'number'
          ? `${duration} ${duration === 1 ? 'Stunde' : 'Stunden'}`
          : 'Paket');

      updateBookingData('packageId', defaultPkg.id);
      updateBookingData('packageName', name);
      updateBookingData('packageDurationHours', duration ?? null);
    }
  }, [isPackagePricing, normalizedPackages, bookingData?.packageId, updateBookingData]);
  
  // Time Slot Generation Functions
  const getAvailableTimeRanges = () => {
    if (!bookingData.date) return [];
    return getAvailabilityRangesForDate(bookingData.date, bookingStudioData);
  };

  const generateTimeSlots = () => {
    if (!bookingData.date) return [];
    
    const timeRanges = getAvailableTimeRanges();
    let allSlots = [];
    
    // Process each time range and collect all valid slots
    timeRanges.forEach(range => {
      const { start, end } = range;
      
      // Parse the start and end times
      const startDate = new Date(`2000-01-01T${start}`);
      const endDate = new Date(`2000-01-01T${end === "00:00" || end === "24:00" ? "24:00" : end}`);
      
      // Calculate the latest possible hour considering minimum booking duration
      const latestStartTime = new Date(endDate);
      latestStartTime.setHours(latestStartTime.getHours() - minHours);
      
      
      // Skip this range if it's too short for minimum booking duration
      if (startDate > latestStartTime) {
        return;
      }
      
      // Generate time slots from the start time up to the latest possible start time (inclusive)
      let current = new Date(startDate);
      while (current <= latestStartTime) {
        const timeString = current.getHours() === 24 ? "24:00" : 
                         `${current.getHours().toString().padStart(2, '0')}:00`;
        
                
        if (
          isBookingRangeAvailable(
            bookingData.date,
            timeString,
            minHours,
            bookingStudioData
          )
        ) {
          allSlots.push(timeString);
        }
        
        current.setHours(current.getHours() + 1);
      }
    });

    // Remove duplicates using Set
    const uniqueSlots = [...new Set(allSlots)];
    
    // Sort the time slots from early to late
    uniqueSlots.sort((a, b) => {
      const [hoursA] = a.split(':').map(Number);
      const [hoursB] = b.split(':').map(Number);
      
      // Special case for 24:00
      if (a === "24:00") return 1;  // 24:00 goes last
      if (b === "24:00") return -1; // 24:00 goes last
      
      // Normal hour comparison
      return hoursA - hoursB;
    });

    return uniqueSlots;
  };

  const generateEndTimeSlots = (startTime) => {
    if (!startTime || !bookingData.date) {
      return [];
    }
    
    const timeRanges = getAvailableTimeRanges();

    if (timeRanges.length === 0) {
      return [];
    }
    
    const slots = [];
    // Use the actual selected date for time calculations to handle timezone properly
    const selectedDate = parseLocalDateString(bookingData.date);
    const startDateTime = new Date(`${bookingData.date}T${startTime}`);

    // Find the specific time range that contains the start time
    // Fix: Use the same time format (HH:00) for comparison
    const startTimeHour = startTime.split(':')[0].padStart(2, '0') + ":00";
    
    
    const currentRange = timeRanges.find(range => {
      // Convert all times to 24-hour format for proper comparison
      const rangeStart = range.start;
      const rangeEnd = range.end;
      
      // Compare as strings directly for hour-based ranges
      const isStartInRange = 
        // Handle special case where range crosses midnight
        (rangeEnd < rangeStart) ? 
          (startTimeHour >= rangeStart || startTimeHour <= rangeEnd) :
          (startTimeHour >= rangeStart && startTimeHour <= rangeEnd);
      
      return isStartInRange;
    });
    
    
    if (!currentRange) {
      return [];  // Start time isn't in any available range
    }
    
    // Set endDateTime to the end of the current time range only
    let endDateTime = new Date(selectedDate);
    if (currentRange.end === "00:00" || currentRange.end === "24:00") {
      endDateTime.setHours(24, 0, 0, 0);
    } else {
      const [endHour, endMinute] = currentRange.end.split(':');
      endDateTime.setHours(parseInt(endHour), parseInt(endMinute), 0, 0);
    }
    
    
    // Start from minHours after the selected start time
    let current = new Date(startDateTime);
    current.setHours(current.getHours() + minHours);

    // If minimum booking puts us past the end time, there are no valid end times
    if (current > endDateTime) {
      return [];
    }

    // Find any bookings that could limit our end time
    const busyIntervalsOnSelectedDate = getBusyIntervalItems(bookingStudioData).filter(interval => {
      if (!interval || !interval.startDateTime || !interval.endDateTime) {
        return false;
      }
      
      const bookingDate = new Date(interval.startDateTime).toDateString();
      const ourDate = selectedDate.toDateString();
      return bookingDate === ourDate;
    }) || [];
    

    // Find the earliest booking that starts after our selected start time
    // but only consider bookings within our current availability range
    let earliestNextBooking = null;
    busyIntervalsOnSelectedDate.forEach(interval => {
      const bookingStart = new Date(interval.startDateTime);
      if (bookingStart > startDateTime && bookingStart <= endDateTime) {
        if (!earliestNextBooking || bookingStart < earliestNextBooking) {
          earliestNextBooking = bookingStart;
        }
      }
    });
    

    // If there's a booking after our start time within our range, limit end times to that booking
    if (earliestNextBooking && earliestNextBooking < endDateTime) {
      endDateTime = earliestNextBooking;
    }

    // Generate all valid end times from minHours after start to the end of the current range
    while (current <= endDateTime) {
      // Always display 24:00 instead of 00:00 for end times
      const timeString = current.getHours() === 0 || current.getHours() === 24 ? "24:00" : 
                       `${current.getHours().toString().padStart(2, '0')}:00`;
      
      
      // Check if this end time would overlap with any existing bookings
      const wouldOverlap = getBusyIntervalItems(bookingStudioData).some(interval => {
        if (!interval || !interval.startDateTime || !interval.endDateTime) {
          return false;
        }
        
        const bookingStart = new Date(interval.startDateTime);
        const bookingEnd = new Date(interval.endDateTime);
        
        // End time is invalid if it's within an existing booking
        // Exception: The exact start time of a booking is valid as an end time
        if (current.getTime() !== bookingStart.getTime() && 
            current > bookingStart && current < bookingEnd) {
          return true;
        }
        
        return false;
      });

      // Check if this end time would overlap with any blocked times
      const wouldOverlapBlocked = bookingStudioData?.blockedTimes?.items?.some(blocked => {
        if (!blocked || !blocked.startDateTime || !blocked.endDateTime) {
          return false;
        }
        
        const blockedStart = new Date(blocked.startDateTime);
        const blockedEnd = new Date(blocked.endDateTime);
        
        // End time is invalid if it's within a blocked time
        // Exception: The exact start time of a blocked period is valid as an end time
        if (current.getTime() !== blockedStart.getTime() && 
            current > blockedStart && current <= blockedEnd) {
          return true;
        }
        
        return false;
      });
      

      const durationHours = (current - startDateTime) / (1000 * 60 * 60);
      const isRangeAvailable = isBookingRangeAvailable(
        bookingData.date,
        startTime,
        durationHours,
        bookingStudioData
      );

      if (!wouldOverlap && !wouldOverlapBlocked && isRangeAvailable) {
        slots.push(timeString);
      }
      
      current.setHours(current.getHours() + 1);
    }

    return slots;
  };

  // Update available times when date changes (hourly mode only)
  useEffect(() => {
    // In package pricing mode, start/end times are controlled by the package modal.
    // Do NOT run hourly normalization here to avoid clearing package endTime.
    if (isPackagePricing) {
      setTimes([]);
      return;
    }

    if (!bookingData.date) {
      setTimes([]);
      return;
    }

    const availableSlots = generateTimeSlots();

    setTimes(availableSlots);

    // If current start time is not available in new slots, reset it
    if (bookingData.startTime && !availableSlots.includes(bookingData.startTime)) {
      setStartTime('');
      updateBookingData('startTime', '');
      setEndTime('');
      updateBookingData('endTime', '');
    } else if (bookingData.startTime) {
      // If start time is valid, update end time options
      const endSlots = generateEndTimeSlots(bookingData.startTime);
      
      // If current end time is not available in new slots, reset it
      if (bookingData.endTime && !endSlots.includes(bookingData.endTime)) {
        setEndTime('');
        updateBookingData('endTime', '');
      }
    }
  }, [
    bookingData?.date,
    bookingData?.endTime,
    bookingData?.startTime,
    bookingStudioData,
    isPackagePricing,
    minHours,
  ]);

  // Initialize booking data from backend when component loads
  // Only run this once when bookingStudioData first loads, not on every change
  useEffect(() => {
    if (!bookingStudioData) return;
    
    // Only set date if it doesn't exist - don't override existing date
    if (!bookingData.date) {
      const today = new Date();
      const durationHoursForDate =
        isPackagePricing ? getBookingDurationHours(bookingStudioData, bookingData) : minHours || 1;
      let selectedDate = null;

      for (let i = 0; i <= 30; i++) {
        const dateToCheck = new Date(today);
        dateToCheck.setDate(today.getDate() + i);

        if (
          hasBookableTimeSlot(dateToCheck, bookingStudioData, {
            durationHours: durationHoursForDate,
          })
        ) {
          selectedDate = dateToCheck;
          break;
        }
      }

      if (selectedDate) {
        setDate(selectedDate);
        updateBookingData('date', formatLocalDate(selectedDate));
      }
    }
    
    if (!bookingData?.numberOfPeople) {
      updateBookingData('numberOfPeople', '1-5');
      updateBookingData('addFreelancer', false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingStudioData]); // Only depend on bookingStudioData, not bookingData to avoid clearing date on package changes

  useEffect(() => {
    setDate(parseLocalDateString(bookingData.date));
    setStartTime(bookingData.startTime || '');
    setEndTime(bookingData.endTime || '');
    setNumberOfPeople(bookingData.numberOfPeople || "1-5");
  }, [bookingData]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if the click was on the date container
      const dateContainer = event.target.closest(`.${classes.dateContainer}`);
      if (dateContainer) {
        return; // Don't close if clicking the date container
      }
      
      // Check if click is inside the datePickerRef
      const isInsideRef = datePickerRef.current && datePickerRef.current.contains(event.target);
      
      // Check if click is on a react-datepicker navigation element
      // react-datepicker navigation buttons have classes like:
      // - react-datepicker__navigation
      // - react-datepicker__navigation--previous
      // - react-datepicker__navigation--next
      const clickedElement = event.target;
      const isDatePickerNavigation = 
        clickedElement.closest('.react-datepicker__navigation') !== null ||
        clickedElement.classList.contains('react-datepicker__navigation') ||
        clickedElement.classList.contains('react-datepicker__navigation-icon') ||
        clickedElement.closest('.react-datepicker') !== null;
      
      // Only close if click is outside both the ref and any DatePicker element
      if (!isInsideRef && !isDatePickerNavigation) {
        setShowDatePicker(false);
      }
    };

    if (showDatePicker) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDatePicker]);

  // Handler for clicks outside people dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (peopleDropdownRef.current && !peopleDropdownRef.current.contains(event.target)) {
        setShowPeopleDropdown(false);
      }
    };

    if (showPeopleDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showPeopleDropdown]);

  const handleStartTimeChange = (e) => {
    const newStartTime = e.target.value;
    setStartTime(newStartTime);
    updateBookingData('startTime', newStartTime);
    
    // Reset end time when start time changes
    setEndTime('');
    updateBookingData('endTime', '');
  };

  const handleEndTimeChange = (e) => {
    const newEndTime = e.target.value;
    setEndTime(newEndTime);
    updateBookingData('endTime', newEndTime);
  };

  const handleNumberOfPeopleChange = (e) => {
    const newNumberOfPeople = e.target.value;
    setNumberOfPeople(newNumberOfPeople);
    updateBookingData('numberOfPeople', newNumberOfPeople);
  };

  // Function to generate people range options based on bookingStudioData
  const generatePeopleOptions = () => {
    // In package mode, derive people groups from studio packages
    if (isPackagePricing && personGroups.length > 0) {
      const currentPackage = getSelectedPackage();
      return personGroups.map((group) => {
        const extraLabel = getAdditionalCostLabelForGroup(currentPackage, group.key);
        return {
          label: `${group.min}-${group.max} Personen${extraLabel}`,
          value: group.key,
        };
      });
    }

    // In hourly mode, prefer explicit tieredPricing groups from backend
    if (!isPackagePricing && Array.isArray(bookingStudioData?.tieredPricing) && bookingStudioData.tieredPricing.length > 0) {
      const capacity = bookingStudioData?.numberOfPeople || 0;
      return bookingStudioData.tieredPricing
        .slice()
        .sort((a, b) => (a.minPersons || 0) - (b.minPersons || 0))
        .map(tier => {
          const min = tier.minPersons;
          const max = tier.maxPersons;
          const clampedMax = capacity ? Math.min(max, capacity) : max;
          const value = `${min}-${clampedMax}`;
          const label = min === clampedMax
            ? `${min} Personen`
            : `${min}-${clampedMax} Personen`;
          return { min, max: clampedMax, label, value };
        });
    }

    const maxPeople = bookingStudioData?.numberOfPeople || 0;
    const ranges = [
      { min: 1, max: 5, label: "1-5 Personen", value: "1-5" },
      { min: 6, max: 10, label: "6-10 Personen", value: "6-10" },
      { min: 11, max: 20, label: "11-20 Personen", value: "11-20" },
      { min: 21, max: 40, label: "21-40 Personen", value: "21-40" },
      { min: 41, max: 60, label: "41-60 Personen", value: "41-60" },
      { min: 61, max: Infinity, label: "60+ Personen", value: "60+" }
    ];

    const availableOptions = [];
    
    for (const range of ranges) {
      if (maxPeople >= range.min) {
        availableOptions.push(range);
      } else {
        break;
      }
    }

    return availableOptions;
  };

  const togglePeopleDropdown = () => {
    setShowPeopleDropdown(!showPeopleDropdown);
  };

  const togglePackageDropdown = () => {
    setShowPackageDropdown((prev) => !prev);
  };

  const handlePackageOptionSelect = (packageId) => {
    if (!isPackagePricing) return;
    const pkg = normalizedPackages.find((p) => p.id === packageId);
    if (!pkg) return;

    const duration = pkg.durationHours;
    const name =
      pkg.name ||
      (typeof duration === 'number'
        ? `${duration} ${duration === 1 ? 'Stunde' : 'Stunden'}`
        : 'Paket');

    // Check if this is actually a different package (not just re-selecting the same one)
    const isPackageChange = bookingData?.packageId !== packageId;

    updateBookingData('packageId', packageId);
    updateBookingData('packageName', name);
    updateBookingData('packageDurationHours', duration);

    // If package changed, clear date and time, and open the date-time selection modal
    if (isPackageChange) {
      updateBookingData('date', '');
      updateBookingData('startTime', '');
      updateBookingData('endTime', '');
      setDate(null);
      setStartTime('');
      setEndTime('');
      
      // Open the package modal for date-time selection if available
      if (setShowPackageModal) {
        if (setPackageModalFromOverlay) {
          setPackageModalFromOverlay(false);
        }
        setShowPackageModal(true);
      }
    }

    // Ensure we have a people group selected
    if (!bookingData?.selectedPeopleGroupKey && personGroups.length > 0) {
      const groupKey = personGroups[0].key;
      updateBookingData('selectedPeopleGroupKey', groupKey);
      updateBookingData(
        'numberOfPeople',
        `${personGroups[0].min}-${personGroups[0].max}`
      );
    }

    // Price calculation is handled by the main useEffect in Booking.jsx
    // which uses calculateStudioPrice and includes all discounts, surcharges, fees, etc.
    // No need to manually calculate here to avoid duplication and inconsistencies.

    setShowPackageDropdown(false);
  };

  const handlePeopleOptionSelect = (optionValue) => {
    if (isPackagePricing && personGroups.length > 0) {
      // Package-based people groups: store key and keep numberOfPeople for fallback views
      updateBookingData('selectedPeopleGroupKey', optionValue);
      const group = personGroups.find((g) => g.key === optionValue);
      if (group) {
        updateBookingData('numberOfPeople', `${group.min}-${group.max}`);
      }

      // Price calculation is handled by the main useEffect in Booking.jsx
      // which uses calculateStudioPrice and includes all discounts, surcharges, fees, etc.
      // The useEffect will automatically recalculate when selectedPeopleGroupKey changes.
    } else {
      setNumberOfPeople(optionValue);
      updateBookingData('numberOfPeople', optionValue);
    }
    setShowPeopleDropdown(false);
  };

  const calculateDuration = (start, end) => {
    if (!start || !end) return 0;
    const startDate = new Date(`2000-01-01T${start}`);
    const normalizedEnd = end === "00:00" || end === "24:00" ? "00:00" : end;
    const endDate = new Date(`2000-01-01T${normalizedEnd}`);
    
    if (end === "00:00" || end === "24:00") {
      endDate.setDate(endDate.getDate() + 1);
    }
    const diffMs = endDate - startDate;
    const diffHours = diffMs / (1000 * 60 * 60);
    
    return diffHours;
  };

  const duration = calculateDuration(bookingData.startTime || startTime, bookingData.endTime || endTime);

  // Helper: get effective hourly price for current people group (tieredPricing aware)
  const getHourlyPriceForCurrentGroup = () => {
    if (!bookingStudioData || isPackagePricing) {
      return studioPrice;
    }

    const tiers = bookingStudioData.tieredPricing;
    if (!Array.isArray(tiers) || tiers.length === 0) {
      return studioPrice;
    }

    // Derive an approximate people count from bookingData.numberOfPeople (e.g. "1-2", "60+")
    let peopleCount = null;
    const rawGroup = bookingData?.numberOfPeople;

    if (typeof rawGroup === 'string') {
      const match = rawGroup.match(/^(\d+)(?:-(\d+)|\+)?$/);
      if (match) {
        const min = parseInt(match[1], 10);
        const max = match[2] ? parseInt(match[2], 10) : min;
        const capacity = typeof bookingStudioData.numberOfPeople === 'number'
          ? bookingStudioData.numberOfPeople
          : parseInt(bookingStudioData.numberOfPeople || '0', 10);
        peopleCount = capacity > 0 ? Math.min(max, capacity) : max;
      }
    } else if (typeof rawGroup === 'number') {
      peopleCount = rawGroup;
    }

    // Fallback to studio capacity or 1 person
    if (!peopleCount || Number.isNaN(peopleCount)) {
      const capacity = typeof bookingStudioData.numberOfPeople === 'number'
        ? bookingStudioData.numberOfPeople
        : parseInt(bookingStudioData.numberOfPeople || '0', 10);
      peopleCount = capacity > 0 ? capacity : 1;
    }

    const matchingTier = tiers.find(tier => {
      const min = typeof tier.minPersons === 'number' ? tier.minPersons : parseInt(tier.minPersons || '0', 10);
      const max = typeof tier.maxPersons === 'number' ? tier.maxPersons : parseInt(tier.maxPersons || '0', 10);
      return peopleCount >= min && peopleCount <= max;
    });

    return matchingTier?.pricePerHour || studioPrice;
  };

  // Keep local totalPriceState in sync with the value calculated
  // in the central pricing logic (StudioProfile/Booking.jsx).
  const calculateTotalPrice = () => {
    const total = bookingData.totalPrice || 0;
    if (totalPriceState !== total) {
      setTotalPriceState(total);
    }
    return total;
  };

  useEffect(() => {
    if (isPackagePricing) return;
    calculateTotalPrice();
  }, [bookingData.totalPrice, isPackagePricing]);

  const handleDatePickerContinue = () => {
    setShowDatePicker(false);
  };

  const toggleDatePicker = () => {
    setShowDatePicker(showDatePicker ? false : true);
  };

  // Calculate available end times based on selected start time
  const availableEndTimes = bookingData.startTime ? generateEndTimeSlots(bookingData.startTime) : [];

  const hasTieredHourlyPricing =
    !isPackagePricing &&
    Array.isArray(bookingStudioData?.tieredPricing) &&
    bookingStudioData.tieredPricing.length > 0;

  const useNumericPeopleInput = !isPackagePricing && !hasTieredHourlyPricing;

  const studioCapacity = useMemo(() => {
    const raw = bookingStudioData?.numberOfPeople;
    if (typeof raw === 'number') return raw || 0;
    const parsed = parseInt(raw || '0', 10);
    return Number.isNaN(parsed) ? 0 : parsed;
  }, [bookingStudioData?.numberOfPeople]);

  const numericPeopleValue = useMemo(() => {
    const raw = bookingData?.numberOfPeople;
    if (typeof raw === 'number') return raw;
    if (typeof raw === 'string') {
      const match = raw.match(/^(\d+)/);
      if (match) {
        const parsed = parseInt(match[1], 10);
        if (!Number.isNaN(parsed)) return parsed;
      }
    }
    return 1;
  }, [bookingData?.numberOfPeople]);

  const handlePeopleNumericChange = (e) => {
    let value = e.target.value;
    if (value === '') {
      updateBookingData('numberOfPeople', '');
      setNumberOfPeople('');
      return;
    }

    let num = parseInt(value, 10);
    if (Number.isNaN(num)) {
      return;
    }

    if (num < 1) num = 1;
    if (studioCapacity && num > studioCapacity) num = studioCapacity;

    updateBookingData('numberOfPeople', num);
    setNumberOfPeople(num);
  };

    return (
        <div className={classes.bookingContainer}>
            <div className={classes.bookingContainerLeft}>
                <h1 className="titleSemibold2">Buchungsdetails</h1>

                {isPackagePricing ? (
                  <>
                    {/* Paket selection (interactive in desktop booking flow) */}
                    <div className={classes.labelsContainer}>
                      <h3 className="semiboldMiddle">Paket</h3>
                      <div className={classes.dateLabelContainer}>
                        <div className={classes.customDropdownContainer}>
                          <div
                            className={`${classes.packageLabelContainer} regularLarge`}
                            onClick={togglePackageDropdown}
                          >
                            <div className={classes.packageContent}>
                              <span>{packageLabel}</span>
                              {packagePriceLabel && (
                                <div className={classes.packagePriceBox}>{packagePriceLabel}</div>
                              )}
                            </div>
                            <svg xmlns="http://www.w3.org/2000/svg" width="15" height="8" viewBox="0 0 15 8" fill="none">
                              <path d="M1 0.86149L7.59865 6.8457M6.85146 6.82992L14 0.845703" stroke="#1F1F1F"/>
                            </svg>
                          </div>
                          {showPackageDropdown && normalizedPackages.length > 0 && (
                            <div className={classes.customDropdownOptions}>
                              {normalizedPackages.map((pkg) => (
                                <div
                                  key={pkg.id}
                                  className={`${classes.customDropdownOption} regularLarge`}
                                  onClick={() => handlePackageOptionSelect(pkg.id)}
                                >
                                  <span>
                                    {pkg.durationHours} {pkg.durationHours === 1 ? 'Stunde' : 'Stunden'}
                                  </span>
                                  <span style={{ marginLeft: '8px' }}>
                                    {(() => {
                                      const basePrice = getPackageBasePrice(pkg);
                                      return basePrice > 0 ? `${basePrice} €` : '';
                                    })()}
                                  </span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Personenanzahl wählen */}
                    <div className={classes.personContainer}>
                      <h3 className="semiboldMiddle">Personenanzahl</h3>
                      <div className={classes.containerPeople}>
                        <div className={classes.customDropdownContainer} ref={peopleDropdownRef}>
                          <div
                            className={`${classes.peopleLabelContainer} regularLarge`}
                            onClick={togglePeopleDropdown}
                          >
                            <label>{peopleLabel}</label>
                            <svg xmlns="http://www.w3.org/2000/svg" width="15" height="8" viewBox="0 0 15 8" fill="none">
                              <path d="M1 0.86149L7.59865 6.8457M6.85146 6.82992L14 0.845703" stroke="#1F1F1F"/>
                            </svg>
                          </div>
                          {showPeopleDropdown && (
                            <div className={classes.customDropdownOptions}>
                              {generatePeopleOptions().map((option) => (
                                <div
                                  key={option?.value}
                                  className={`${classes.customDropdownOption} regularLarge`}
                                  onClick={() => handlePeopleOptionSelect(option?.value)}
                                >
                                  {option?.label}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                {/* Datum & Uhrzeit summary with Bearbeiten */}
                    <div className={classes.dateTimeContainer} style={{ marginTop: "32px" }}>
                      <h3 className="semiboldMiddle">Datum &amp; Uhrzeit</h3>
                      <div
                       className={classes.dateContainer}
                       onClick={
                        setShowPackageModal
                          ? () => {
                              if (setPackageModalFromOverlay) {
                                setPackageModalFromOverlay(true);
                              }
                              setShowPackageModal(true);
                            }
                          : toggleDatePicker
                       }
                      >
                        <div>
                          <p className="regularMiddle">
                            {bookingData.date
                              ? format(parseLocalDateString(bookingData.date), 'EEEE, dd. MMMM, yyyy', { locale: de })
                              : 'Datum wählen'}
                            {bookingData.startTime && bookingData.endTime && (
                              <> · {bookingData.startTime} - {bookingData.endTime} Uhr</>
                            )}
                          </p>
                        </div>
                        <span
                          className={`${classes.editButton} slimRegularSmallText`}
                        >
                          Bearbeiten
                        </span>
                      </div>

                       {/* In package mode the detailed Datum & Uhrzeit selection
                           happens in the package modal controlled by the parent.
                           The inline DateChooser is only used as a fallback when
                           no modal handler is provided. */}
                       {!setShowPackageModal && showDatePicker && (
                         <div className={classes.datePickerOverlay} ref={datePickerRef}>
                           <DateChooser
                             fromDesktop={true}
                             nextSlide={handleDatePickerContinue}
                             bookingStudioData={bookingStudioData}
                           />
                         </div>
                       )}
                    </div>
                  </>
                ) : (
                  <>
                    <div className={classes.dateTimeContainer}>
                      <h3 className="semiboldMiddle">Datum & Uhrzeit</h3>
                      <div className={classes.dateInput}>
                          <div 
                              className={classes.dateContainer}
                              onClick={toggleDatePicker}
                              >
                              {bookingData.date ? format(parseLocalDateString(bookingData.date), 'dd.MM.yyyy', { locale: de }) : 'Datum wählen'}
                              <svg xmlns="http://www.w3.org/2000/svg" width="15" height="8" viewBox="0 0 15 8" fill="none">
                                  <path d="M1 1.01579L7.59865 7M6.85146 6.98421L14 1" stroke="#1F1F1F"/>
                              </svg >
                          </div>
                          {showDatePicker &&
                              <div className={classes.datePickerOverlay} ref={datePickerRef}>
                              <DateChooser 
                                  fromDesktop={true}
                                  nextSlide={handleDatePickerContinue} 
                                  bookingStudioData={bookingStudioData} 
                              />
                              </div>
                          }
                      </div>

                      <div className={`${classes.timeOptionContainer} regularLarge`}>
                          <div className={classes.labelContainer}>
                              <select 
                              className={`${classes.select} regularLarge ${classes.smaller}`} 
                              id="start" 
                              value={bookingData.startTime} 
                              onChange={handleStartTimeChange}
                              >
                              <option value="">Start</option>
                              {times.map((time, index) => (
                                  <option key={index} value={time}>{time}</option>
                              ))}
                              </select>
                          </div>
                          <div className={classes.verticalLine}></div>
                          <div className={classes.labelContainer}>
                              <select 
                                  className={`${classes.select} regularLarge ${classes.smaller}`} 
                                  id="end" 
                                  value={bookingData.endTime} 
                                  onChange={handleEndTimeChange} 
                                  disabled={!bookingData.startTime}
                              >
                                  <option value="">Ende</option>
                                  {availableEndTimes.map((time, index) => (
                                      <option key={index} value={time}>{time}</option>
                                  ))}
                              </select>
                          </div>
                      </div>
                      {duration !== 0 &&
                      <div className={classes.textContainer}>
                          {/* <span className={classes.textContainerSpan}>Ein Tag mehr hinzufügen</span> */}
                          <p className="regularXxs">Dauer: {duration} Stunden</p>
                      </div>
                      }
                    </div>
                    <div className={classes.personContainer}>
                        <h3 className="semiboldMiddle">Personenanzahl</h3>
                        <div className={classes.containerPeople}>
                            {useNumericPeopleInput ? (
                              <div className={classes.customDropdownContainer}>
                                <div className={`${classes.peopleLabelContainer} regularLarge`}>
                                  <label htmlFor="peopleCountInputDesktop">
                                    Personenanzahl
                                  </label>
                                  <input
                                    id="peopleCountInputDesktop"
                                    type="number"
                                    className={`${classes.personInput} regularLarge`}
                                    min={1}
                                    style={{textAlign: 'right'}}
                                    max={studioCapacity || undefined}
                                    value={numericPeopleValue === '' ? '' : numericPeopleValue}
                                    onChange={handlePeopleNumericChange}
                                  />
                                </div>
                              </div>
                            ) : (
                              <div className={classes.customDropdownContainer} ref={peopleDropdownRef}>
                                <div 
                                  className={`${classes.peopleLabelContainer} regularLarge`}
                                  onClick={togglePeopleDropdown}
                                >
                                  <label>
                                    Personenanzahl:{" "}
                                    {(() => {
                                      const current = bookingData?.numberOfPeople;
                                      const options = generatePeopleOptions();
                                      const match = options.find(o => o.value === current);
                                      if (match) return match.label;
                                      if (options.length > 0) return options[0].label;
                                      return current || "1-5 Personen";
                                    })()}
                                  </label>
                                  <svg xmlns="http://www.w3.org/2000/svg" width="15" height="8" viewBox="0 0 15 8" fill="none">
                                    <path d="M1 0.86149L7.59865 6.8457M6.85146 6.82992L14 0.845703" stroke="#1F1F1F"/>
                                  </svg>
                                </div>
                                {showPeopleDropdown && (
                                  <div className={classes.customDropdownOptions}>
                                    {generatePeopleOptions().map((option) => (
                                      <div 
                                        key={option?.value} 
                                        className={`${classes.customDropdownOption} regularLarge`}
                                        onClick={() => handlePeopleOptionSelect(option?.value)}
                                      >
                                        {option?.label}
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            )}
                        </div>
                    </div>
                  </>
                )}
            </div>
            <div className={classes.bookingContainerRight}>
                <TotalBox
                    studio={studio}
                    studioCreativesData={studioCreativesData}
                />
            </div>
        </div>
    )
}

export default BookingFirst;
